/**
 * Tree operations on `DesignNode[]` — find, insert, move, clone, reorder.
 *
 * Pure functions, no Angular. Every mutator takes the node array and mutates it
 * in place; the store always hands them a deep clone of the active page, so
 * "in place" never touches the state the UI is currently rendering.
 */

import {
  CONTAINER_NODE_TYPES,
  type DesignNode,
  type DesignNodeType,
  type PaletteItem,
  type Point,
  type Rect,
} from '../design-builder.types';
import {
  DEFAULT_AUTO_LAYOUT,
  DEFAULT_ICON,
  DEFAULT_INPUT_WIDGET_STYLE,
  DEFAULT_ZONE_SLIDER_STYLE,
  DEFAULT_STYLE,
  DEFAULT_TEXT,
  DEFAULT_WIDGET_STYLE,
  DUPLICATE_OFFSET,
  SCREEN_FRAME_HEIGHT,
  SCREEN_FRAME_KEY,
} from '../design-builder.constants';
import { withSampleData } from '../renderers/design-sample-data';
import { defaultAppearance } from '../renderers/design-appearance.spec';
import { roundGeometry } from './design-geometry.utils';

let idCounter = 0;

/** Collision-proof within a session, and short enough to read in the JSON pane. */
export function createNodeId(): string {
  idCounter += 1;
  return `nd_${Date.now().toString(36)}${idCounter.toString(36)}`;
}

/** A componentId for a text registered as a KPI component. The `KPI` in the
 *  name is load-bearing: `resolveRecomputeChartType` falls back to the id when
 *  a compute response omits `chartType`. */
export function createTextComponentId(): string {
  idCounter += 1;
  return `CMP-KPI-TEXT-${idCounter}-${Math.floor(100 + Math.random() * 900)}`;
}

export function createPageId(): string {
  return `pg_${Date.now().toString(36)}${(idCounter += 1).toString(36)}`;
}

export function createDocumentId(): string {
  return `dsg_${Date.now().toString(36)}${(idCounter += 1).toString(36)}`;
}

export function isContainer(node: DesignNode): boolean {
  return CONTAINER_NODE_TYPES.includes(node.type);
}

/**
 * Node types that can hold children.
 *
 * Not just frames: drawing three small rectangles inside a big one should nest
 * them, because that is what "inside" means to the person drawing it. Shapes
 * are therefore containers too. Leaves (text, line, image, icon) and WIDGETs
 * are not — a chart has no meaningful inside.
 */
export function acceptsChildren(node: DesignNode): boolean {
  // Only a screen owns children. Shapes used to take them too, which is how a
  // panel rectangle ended up owning half a screen, and a GROUP now references
  // its members instead of holding them — so neither is a drop target.
  return node.type === 'FRAME';
}

/** A container that can take a new child, with its origin in ROOT space. */
export interface ContainerHit {
  id: string;
  absoluteX: number;
  absoluteY: number;
}

/**
 * The DEEPEST container under `point`, at any nesting depth.
 *
 * Walks siblings back-to-front (the last one paints on top, so it wins the hit
 * test) and recurses before testing the node itself, so a rectangle inside a
 * frame inside a screen nests into the rectangle rather than the screen. A node
 * that contains the point but cannot hold children is skipped, and its parent
 * is considered instead.
 *
 * Returns the container's absolute origin so the caller can rebase the new
 * child's coordinates, which are parent-relative.
 */
export function findContainerAt(
  nodes: readonly DesignNode[],
  point: Point,
  excludeId: string | null = null,
  offsetX = 0,
  offsetY = 0,
): ContainerHit | null {
  for (let index = nodes.length - 1; index >= 0; index--) {
    const node = nodes[index];
    if (node.id === excludeId || !node.visible || node.locked) continue;

    const x = offsetX + node.x;
    const y = offsetY + node.y;
    const inside =
      point.x >= x && point.x <= x + node.width && point.y >= y && point.y <= y + node.height;
    if (!inside) continue;

    if (node.children?.length) {
      const deeper = findContainerAt(node.children, point, excludeId, x, y);
      if (deeper) return deeper;
    }
    if (acceptsChildren(node)) return { id: node.id, absoluteX: x, absoluteY: y };
  }
  return null;
}

// ── Creation ─────────────────────────────────────────────────────────────────

/** A fresh node for a palette item, positioned at `at` (its top-left). */
export function createNodeFromPalette(item: PaletteItem, at: Point): DesignNode {
  const node: DesignNode = {
    id: createNodeId(),
    name: item.label,
    type: item.nodeType,
    x: roundGeometry(at.x),
    y: roundGeometry(at.y),
    width: item.defaultWidth,
    height: item.defaultHeight,
    rotation: 0,
    visible: true,
    locked: false,
    opacity: 1,
    style: cloneStyleFor(item.nodeType),
  };

  if (item.nodeType === 'FRAME') {
    node.children = [];
    node.layout = { ...DEFAULT_AUTO_LAYOUT, padding: [...DEFAULT_AUTO_LAYOUT.padding] };
    node.style.overflow = 'hidden';
    // A Screen is a dashboard page: full width, pinned to the left edge.
    if (item.key === SCREEN_FRAME_KEY) {
      node.fluid = true;
      node.x = 0;
      node.name = 'Screen';
    }
  }
  if (item.nodeType === 'GROUP') node.children = [];
  if (item.nodeType === 'TEXT') node.text = { ...DEFAULT_TEXT };
  if (item.nodeType === 'IMAGE') node.image = { src: '', fit: 'cover', alt: '' };
  if (item.nodeType === 'ICON') node.icon = { ...DEFAULT_ICON };
  if (item.nodeType === 'WIDGET' && item.layoutType) {
    // Form controls bring their own chrome — a card around them would double
    // the border and clip the control.
    if (item.layoutType === 'ZONE_SLIDER') {
      node.style = JSON.parse(JSON.stringify(DEFAULT_ZONE_SLIDER_STYLE)) as DesignNode['style'];
    } else if (item.group === 'inputs') {
      node.style = JSON.parse(JSON.stringify(DEFAULT_INPUT_WIDGET_STYLE)) as DesignNode['style'];
    }
    // Seeded from the node id, so a dropped chart shows a real picture
    // immediately and looks identical every time the design is reopened.
    node.widget = withSampleData(
      {
        layoutType: item.layoutType,
        data: null,
        // Design tier seeded with the family's default variant/orientation, so
        // the dropped chart already renders in a sensible shape.
        appearance: defaultAppearance(item.layoutType),
        binding: {},
        format: {},
        listenerNodeIds: [],
      },
      node.id,
      node.name,
    );
  }
  return node;
}

/** Defaults differ per type — a text node has no card chrome, a widget does. */
function cloneStyleFor(type: DesignNodeType): DesignNode['style'] {
  if (type === 'WIDGET') {
    return JSON.parse(JSON.stringify(DEFAULT_WIDGET_STYLE)) as DesignNode['style'];
  }
  if (type === 'LINE') {
    // A line IS its stroke — no fill, and a visible default so it shows on drop.
    return { fill: { kind: 'none' }, stroke: { color: '#98a2b3', width: 1, style: 'solid' }, radius: 0, shadow: null };
  }
  // Text, icons and groups ARE their content — a white card behind them would
  // be wrong, so they start fully transparent.
  if (type === 'TEXT' || type === 'GROUP' || type === 'ICON') {
    return { fill: { kind: 'none' }, stroke: null, radius: 0, shadow: null };
  }
  return JSON.parse(JSON.stringify(DEFAULT_STYLE)) as DesignNode['style'];
}

/**
 * The single default screen every design starts with.
 *
 * Fluid by construction: a dashboard fills whatever viewport it is published
 * into, so the width is only the width it is being DESIGNED at.
 */
export function createScreenFrame(width: number, height = SCREEN_FRAME_HEIGHT): DesignNode {
  return {
    id: createNodeId(),
    name: 'Screen',
    type: 'FRAME',
    x: 0,
    y: 0,
    width,
    height,
    rotation: 0,
    visible: true,
    locked: false,
    opacity: 1,
    fluid: true,
    style: {
      fill: { kind: 'solid', color: '#ffffff' },
      stroke: null,
      radius: 0,
      // The page lifts off the workspace — the artboard draws nothing now, so
      // this is what separates the screen from the grid behind it.
      shadow: { x: 0, y: 4, blur: 24, spread: 0, color: 'rgba(16, 24, 40, 0.12)' },
      overflow: 'hidden',
    },
    layout: { ...DEFAULT_AUTO_LAYOUT, padding: [...DEFAULT_AUTO_LAYOUT.padding] },
    children: [],
  };
}

export function cloneNode(node: DesignNode): DesignNode {
  return JSON.parse(JSON.stringify(node)) as DesignNode;
}

/** Deep clone with every id in the subtree regenerated — for duplicate/paste. */
export function cloneNodeWithNewIds(node: DesignNode): DesignNode {
  const copy = cloneNode(node);
  const ids = new Map<string, string>();
  reassignIds(copy, ids);
  remapSlideStops(copy, ids);
  reassignComponentIds(copy);
  return copy;
}

/**
 * A copy is a separate backend component: it gets its own componentId. Kept,
 * the copy and the original shared ONE definition in the component store, so
 * a copied dropdown re-pointed at another column (Regions) still listed the
 * original's values (customer names).
 */
function reassignComponentIds(node: DesignNode): void {
  const id = componentIdOf(node);
  if (node.widget && id) node.widget = withComponentId(node.widget, id, freshComponentId(id, node.id));
  if (node.dataBinding?.componentId) node.dataBinding = { ...node.dataBinding, componentId: createTextComponentId() };
  node.children?.forEach(reassignComponentIds);
}

/** The componentId a widget node is registered under (its own, else its build's). */
function componentIdOf(node: DesignNode): string {
  const widget = node.widget;
  if (!widget) return '';
  return String(widget.componentId || widget.build?.['componentId'] || '');
}

/** `<id>-<nodeId>`, replacing a node suffix an earlier copy added (no ever-growing ids). */
function freshComponentId(componentId: string, nodeId: string): string {
  return `${componentId.replace(/-nd_[a-z0-9]+$/i, '')}-${nodeId}`;
}

/** The widget under a new componentId: on the widget, its build and the build's payload. */
function withComponentId(widget: NonNullable<DesignNode['widget']>, from: string, to: string): NonNullable<DesignNode['widget']> {
  const build = widget.build;
  const payload = build?.['payload'] as Record<string, unknown> | undefined;
  return {
    ...widget,
    componentId: to,
    ...(build
      ? {
          build: {
            ...build,
            ...(build['componentId'] === from ? { componentId: to } : {}),
            ...(payload && payload['componentId'] === from ? { payload: { ...payload, componentId: to } } : {}),
          },
        }
      : {}),
  };
}

/**
 * Designs saved before copies got their own componentId: every widget (or bound
 * text) whose componentId an earlier one already uses gets its own -
 * `<id>-<nodeId>`, the same on every load, so it is registered once and stays.
 */
export function withUniqueComponentIds<P extends { nodes: DesignNode[] }>(pages: P[]): P[] {
  const seen = new Set<string>();
  const fix = (node: DesignNode): DesignNode => {
    let next = node;
    const id = componentIdOf(node);
    if (id) {
      if (seen.has(id)) next = { ...next, widget: withComponentId(next.widget!, id, freshComponentId(id, node.id)) };
      seen.add(componentIdOf(next));
    }
    const textId = next.dataBinding?.componentId;
    if (textId) {
      if (seen.has(textId)) next = { ...next, dataBinding: { ...next.dataBinding!, componentId: `${textId}-${node.id}` } };
      seen.add(next.dataBinding!.componentId!);
    }
    return next.children?.length ? { ...next, children: next.children.map(fix) } : next;
  };
  return pages.map((page) => ({ ...page, nodes: page.nodes.map(fix) }));
}

function reassignIds(node: DesignNode, ids: Map<string, string>): void {
  const id = createNodeId();
  ids.set(node.id, id);
  node.id = id;
  node.children?.forEach((child) => reassignIds(child, ids));
}

/** A copied slider must move between the copied stops, not the originals. */
function remapSlideStops(node: DesignNode, ids: Map<string, string>): void {
  if (node.slide) {
    node.slide = {
      ...node.slide,
      stopIds: node.slide.stopIds.map((id) => ids.get(id) ?? id),
      ...(node.slide.followerIds ? { followerIds: node.slide.followerIds.map((id) => ids.get(id) ?? id) } : {}),
    };
  }
  node.children?.forEach((child) => remapSlideStops(child, ids));
}

/** A duplicate, offset so it does not land exactly on the original. */
export function duplicateNode(node: DesignNode): DesignNode {
  const copy = cloneNodeWithNewIds(node);
  copy.x += DUPLICATE_OFFSET;
  copy.y += DUPLICATE_OFFSET;
  copy.name = `${node.name} copy`;
  return copy;
}

// ── Lookup ───────────────────────────────────────────────────────────────────

export function findNode(nodes: DesignNode[], id: string): DesignNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children?.length) {
      const hit = findNode(node.children, id);
      if (hit) return hit;
    }
  }
  return null;
}

export interface NodeLocation {
  parent: DesignNode | null;
  siblings: DesignNode[];
  index: number;
  node: DesignNode;
}

/** Where a node sits: its parent (null at root), its sibling array and index. */
export function locateNode(
  nodes: DesignNode[],
  id: string,
  parent: DesignNode | null = null,
): NodeLocation | null {
  const index = nodes.findIndex((n) => n.id === id);
  if (index >= 0) return { parent, siblings: nodes, index, node: nodes[index] };
  for (const node of nodes) {
    if (!node.children?.length) continue;
    const hit = locateNode(node.children, id, node);
    if (hit) return hit;
  }
  return null;
}

/** Root → node, excluding the node itself. */
export function ancestorsOf(nodes: DesignNode[], id: string): DesignNode[] {
  const trail: DesignNode[] = [];
  const walk = (list: DesignNode[], chain: DesignNode[]): boolean => {
    for (const node of list) {
      if (node.id === id) {
        trail.push(...chain);
        return true;
      }
      if (node.children?.length && walk(node.children, [...chain, node])) return true;
    }
    return false;
  };
  walk(nodes, []);
  return trail;
}

/** A node's top-left in ROOT space (its own x/y plus every ancestor's). */
export function absoluteOrigin(nodes: DesignNode[], id: string): Point | null {
  const node = findNode(nodes, id);
  if (!node) return null;
  return ancestorsOf(nodes, id).reduce(
    (point, ancestor) => ({ x: point.x + ancestor.x, y: point.y + ancestor.y }),
    { x: node.x, y: node.y },
  );
}

export function flattenNodes(nodes: DesignNode[]): DesignNode[] {
  const out: DesignNode[] = [];
  const walk = (list: DesignNode[]): void => {
    for (const node of list) {
      out.push(node);
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  return out;
}

/** True when `ancestorId` is `nodeId` or one of its ancestors — used to block
 *  dropping a frame into its own subtree. */
export function isSelfOrDescendant(nodes: DesignNode[], ancestorId: string, nodeId: string): boolean {
  if (ancestorId === nodeId) return true;
  const ancestor = findNode(nodes, ancestorId);
  if (!ancestor?.children?.length) return false;
  return !!findNode(ancestor.children, nodeId);
}

// ── Mutation ─────────────────────────────────────────────────────────────────

/** Insert `node` into `parentId`'s children (root when null), at `index` or end. */
export function insertNode(
  nodes: DesignNode[],
  node: DesignNode,
  parentId: string | null,
  index?: number,
): void {
  const target = parentId ? findNode(nodes, parentId) : null;
  const list = target ? (target.children ??= []) : nodes;
  const at = index === undefined || index < 0 || index > list.length ? list.length : index;
  list.splice(at, 0, node);
}

/** Remove a node and return it, or null when the id is not in the tree. */
export function removeNode(nodes: DesignNode[], id: string): DesignNode | null {
  const location = locateNode(nodes, id);
  if (!location) return null;
  return location.siblings.splice(location.index, 1)[0] ?? null;
}

export function removeNodes(nodes: DesignNode[], ids: Iterable<string>): DesignNode[] {
  const removed: DesignNode[] = [];
  for (const id of ids) {
    const node = removeNode(nodes, id);
    if (node) removed.push(node);
  }
  return removed;
}

export function renameNode(nodes: DesignNode[], id: string, name: string): void {
  const node = findNode(nodes, id);
  if (node) node.name = name;
}

/** Apply a patch to one node. Nested objects are merged one level deep so
 *  `{ style: { fill } }` does not wipe the rest of the style. */
export function patchNode(nodes: DesignNode[], id: string, patch: Partial<DesignNode>): void {
  const node = findNode(nodes, id);
  if (!node) return;
  for (const [key, value] of Object.entries(patch)) {
    const typed = key as keyof DesignNode;
    const current = node[typed];
    if (isPlainObject(current) && isPlainObject(value)) {
      Object.assign(current as object, value);
    } else {
      (node as unknown as Record<string, unknown>)[key] = value;
    }
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

// ── Reordering / z-order ─────────────────────────────────────────────────────

/**
 * Move a node into a new parent at a new index. No-ops when the target is the
 * node itself or inside its own subtree (which would detach the tree).
 */
export function moveNode(
  nodes: DesignNode[],
  id: string,
  nextParentId: string | null,
  index?: number,
): boolean {
  if (nextParentId && isSelfOrDescendant(nodes, id, nextParentId)) return false;
  const node = removeNode(nodes, id);
  if (!node) return false;
  insertNode(nodes, node, nextParentId, index);
  return true;
}

export type ZOrderCommand = 'forward' | 'backward' | 'front' | 'back';

/** Later in the array renders on top, so "forward" means +1 index. */
export function changeZOrder(nodes: DesignNode[], id: string, command: ZOrderCommand): void {
  const location = locateNode(nodes, id);
  if (!location) return;
  const { siblings, index } = location;
  const next =
    command === 'forward' ? Math.min(siblings.length - 1, index + 1)
    : command === 'backward' ? Math.max(0, index - 1)
    : command === 'front' ? siblings.length - 1
    : 0;
  if (next === index) return;
  const [moved] = siblings.splice(index, 1);
  siblings.splice(next, 0, moved);
}

// ── Grouping ─────────────────────────────────────────────────────────────────

/**
 * Hold the given nodes together with a new GROUP. Only nodes that share a
 * parent can be grouped (mirrors Figma).
 *
 * The group REFERENCES them: nothing is moved or re-based, so each member stays
 * a child of the screen in its own layer and the group is a handle that drags
 * and resizes them as one. Returns the new group, or null when the selection is
 * not groupable.
 */
export function groupNodes(nodes: DesignNode[], ids: string[]): DesignNode | null {
  if (ids.length < 2) return null;

  let locations = ids
    .map((id) => locateNode(nodes, id))
    .filter((location): location is NodeLocation => !!location);
  if (locations.length !== ids.length) return null;

  // A selection spread over different parents (some layers inside the screen,
  // some loose on the page over it) used to be refused without a word, so
  // Group did nothing. Bring the stragglers into the parent most of them
  // share, re-based so nothing moves on screen.
  const siblings = commonSiblings(locations);
  if (!locations.every((location) => location.siblings === siblings)) {
    const parent = locations.find((location) => location.siblings === siblings)!.parent;
    const parentOrigin = parent ? absoluteOrigin(nodes, parent.id) ?? { x: 0, y: 0 } : { x: 0, y: 0 };
    for (const location of locations) {
      if (location.siblings === siblings) continue;
      const origin = absoluteOrigin(nodes, location.node.id);
      if (!origin) return null;
      location.siblings.splice(location.siblings.indexOf(location.node), 1);
      location.node.x = origin.x - parentOrigin.x;
      location.node.y = origin.y - parentOrigin.y;
      siblings.push(location.node);
    }
    locations = ids
      .map((id) => locateNode(nodes, id))
      .filter((location): location is NodeLocation => !!location);
  }

  // Keep the on-canvas stacking order rather than the click order.
  const ordered = [...locations].sort((a, b) => a.index - b.index);
  const members = ordered.map((location) => location.node);
  const bounds = unionBounds(members);

  const group: DesignNode = {
    id: createNodeId(),
    name: 'Group',
    type: 'GROUP',
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    rotation: 0,
    visible: true,
    locked: false,
    opacity: 1,
    style: { fill: { kind: 'none' }, stroke: null, radius: 0, shadow: null },
    // A reference, not a container: the members stay exactly where they are,
    // in the band their own kind belongs to.
    memberIds: members.map((member) => member.id),
  };
  siblings.push(group);
  return group;
}

/** The sibling list most of the locations share (ties: the first one's). */
function commonSiblings(locations: readonly NodeLocation[]): DesignNode[] {
  const counts = new Map<DesignNode[], number>();
  for (const location of locations) counts.set(location.siblings, (counts.get(location.siblings) ?? 0) + 1);
  let best = locations[0].siblings;
  for (const [list, count] of counts) if (count > (counts.get(best) ?? 0)) best = list;
  return best;
}

/**
 * Dissolve a GROUP: the reference goes, the members stay exactly where they
 * are. Returns them, so the caller can leave them selected.
 */
export function ungroupNode(nodes: DesignNode[], id: string): DesignNode[] {
  const location = locateNode(nodes, id);
  if (!location) return [];
  const { node, siblings, index } = location;
  if (node.type !== 'GROUP') return [];
  const members = (node.memberIds ?? [])
    .map((memberId) => findNode(nodes, memberId))
    .filter((member): member is DesignNode => !!member);
  if (!members.length) return [];

  // Dissolving an inner group must leave its members in the outer group.
  for (const sibling of siblings) {
    if (sibling.id === id || !sibling.memberIds?.includes(id)) continue;
    sibling.memberIds = [...new Set(sibling.memberIds.flatMap((memberId) =>
      memberId === id ? members.map((member) => member.id) : [memberId],
    ))];
  }
  siblings.splice(index, 1);
  return members;
}

/** Smallest rect covering every box, in their shared coordinate space.
 *  Takes `Rect` so it works on nodes (structurally rects) and on the absolute
 *  rects the canvas computes for a multi-selection. */
export function unionBounds(boxes: readonly Rect[]): Rect {
  if (!boxes.length) return { x: 0, y: 0, width: 0, height: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const box of boxes) {
    minX = Math.min(minX, box.x);
    minY = Math.min(minY, box.y);
    maxX = Math.max(maxX, box.x + box.width);
    maxY = Math.max(maxY, box.y + box.height);
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
